/**
 * CategoryManagerDialog — create / rename / recolor / delete the holder's smart
 * categories (the set that powers "By Category", the categorize AI, and the
 * filter `set_category` action). Owns its own CRUD via the category mutation
 * hooks. Deleting a category moves its member threads to Uncategorized
 * (ON DELETE SET NULL server-side); the server rejects a duplicate name.
 */

import { useState, type FormEvent } from 'react';
import { Trash2, Plus, Loader2, Check, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { EmailCategoryDto } from '@/lib/api';
import {
  useEmailCategories,
  useCreateEmailCategory,
  useUpdateEmailCategory,
  useDeleteEmailCategory,
  useSetAccountAutoCategorize,
} from '../hooks/useMailbox';
import { useEmailConnections } from '../hooks/useConnectAccount';

const DEFAULT_NEW_COLOR = '#2563EB';

/** One editable category row: recolor (native picker) + rename-on-commit + delete. */
function CategoryRow({ category }: { category: EmailCategoryDto }) {
  const updateCat = useUpdateEmailCategory();
  const deleteCat = useDeleteEmailCategory();
  const [name, setName] = useState(category.name);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const trimmed = name.trim();
  const dirty = trimmed.length > 0 && trimmed !== category.name;
  const commitName = () => {
    if (dirty) updateCat.mutate({ categoryId: category.categoryId, name: trimmed });
    else setName(category.name); // snap back an empty/unchanged edit
  };

  return (
    <div className="flex items-center gap-2 rounded-md border px-2 py-1.5">
      <input
        type="color"
        aria-label={`Colour for ${category.name}`}
        title="Change colour"
        value={category.color}
        onChange={(e) =>
          updateCat.mutate({ categoryId: category.categoryId, color: e.target.value })
        }
        className="h-6 w-6 shrink-0 cursor-pointer rounded border bg-transparent p-0"
      />
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commitName}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            setName(category.name);
          }
        }}
        maxLength={64}
        aria-label={`Category name (${category.name})`}
        className="h-7 flex-1 text-sm"
      />
      {confirmDelete ? (
        <span className="flex items-center gap-1">
          <span className="text-[11px] text-muted-foreground">Delete?</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive"
            aria-label={`Confirm delete ${category.name}`}
            disabled={deleteCat.isPending}
            onClick={() => deleteCat.mutate(category.categoryId)}
          >
            {deleteCat.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            aria-label="Cancel delete"
            onClick={() => setConfirmDelete(false)}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </span>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
          aria-label={`Delete ${category.name}`}
          onClick={() => setConfirmDelete(true)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

/** Per-account "auto-categorize new mail" opt-in (mirrors the AI spam filter —
 *  a per-account `modules` flag). Reads the current value off the connection. */
function AutoCategorizeSection() {
  const { data: connections } = useEmailConnections();
  const setAuto = useSetAccountAutoCategorize();
  if (!connections || connections.length === 0) return null;
  return (
    <section className="border-t pt-3">
      <p className="text-sm font-medium">Auto-categorize new mail</p>
      <p className="mb-2 text-xs text-muted-foreground">
        Automatically sort newly-synced inbox mail into a category using AI (uses credits). Off by
        default.
      </p>
      <div className="flex flex-col gap-1">
        {connections.map((c) => {
          const enabled =
            (c.modules as { autoCategorizeEnabled?: boolean })?.autoCategorizeEnabled === true;
          return (
            <label key={c.accountId} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={enabled}
                disabled={setAuto.isPending}
                onChange={(e) =>
                  setAuto.mutate({ accountId: c.accountId, enabled: e.target.checked })
                }
              />
              <span className="truncate">{c.emailAddress}</span>
            </label>
          );
        })}
      </div>
    </section>
  );
}

export interface CategoryManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CategoryManagerDialog({ open, onOpenChange }: CategoryManagerDialogProps) {
  const { data: categories, isLoading } = useEmailCategories(open);
  const createCat = useCreateEmailCategory();
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_NEW_COLOR);

  const trimmedNew = newName.trim();
  const addCategory = (e: FormEvent) => {
    e.preventDefault();
    if (!trimmedNew) return;
    createCat.mutate(
      { name: trimmedNew, color: newColor },
      {
        onSuccess: () => {
          setNewName('');
          setNewColor(DEFAULT_NEW_COLOR);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage categories</DialogTitle>
          <DialogDescription>
            Categories organise your inbox in the By Category view and power auto-categorize.
            Deleting one moves its mail to Uncategorized.
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 flex max-h-[45vh] flex-col gap-1.5 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading categories…
            </div>
          ) : !categories || categories.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No categories yet — add one below.
            </p>
          ) : (
            categories.map((c) => <CategoryRow key={c.categoryId} category={c} />)
          )}
        </div>

        {/* Add a new category. */}
        <form onSubmit={addCategory} className="flex items-center gap-2 border-t pt-3">
          <input
            type="color"
            aria-label="New category colour"
            title="Pick a colour"
            value={newColor}
            onChange={(e) => setNewColor(e.target.value)}
            className="h-8 w-8 shrink-0 cursor-pointer rounded border bg-transparent p-0"
          />
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New category name"
            maxLength={64}
            aria-label="New category name"
            className="h-8 flex-1"
          />
          <Button
            type="submit"
            size="sm"
            className="h-8 gap-1"
            disabled={!trimmedNew || createCat.isPending}
          >
            {createCat.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            Add
          </Button>
        </form>

        <AutoCategorizeSection />
      </DialogContent>
    </Dialog>
  );
}
